import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, ArrowRight } from 'lucide-react'

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '/services' },
  { label: 'GCC Practice', href: '/gcc-practice' },
  { label: 'Capabilities', href: '/capabilities' },
  { label: 'Insights', href: '/insights' },
  { label: 'Contact Us', href: '/contact' },
]

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => { setMenuOpen(false) }, [location])

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-white border-b border-[#E5E7EB]' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 h-16 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex items-center group">
          <span
            data-testid="navbar-logo-text"
            className="font-heading font-semibold text-[#0B1F3A] leading-none select-none group-hover:opacity-70 transition-opacity duration-200"
            style={{ fontSize: 'clamp(17px, 1.6vw, 21px)', letterSpacing: '-0.02em' }}
          >
            Strive Partners
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map(link => (
            <Link
              key={link.href}
              to={link.href}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              className="text-[10px] tracking-[0.18em] uppercase text-[#0A0A0A]/50 hover:text-[#0B1F3A] transition-colors duration-200 font-body font-semibold"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex">
          <Link
            to="/contact"
            data-testid="navbar-cta"
            className="inline-flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase bg-[#0A0A0A] text-white px-5 py-3 rounded-none hover:bg-[#0B1F3A] transition-colors duration-300 font-body font-semibold"
          >
            Start Search <ArrowRight size={11} />
          </Link>
        </div>

        <button
          data-testid="navbar-mobile-menu-btn"
          className="md:hidden text-[#0A0A0A] p-1"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
        </button>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            data-testid="navbar-mobile-menu"
            className="md:hidden bg-white border-t border-[#E5E7EB] overflow-hidden"
          >
            <nav className="px-6 py-8 space-y-0">
              {navLinks.map((link, i) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="block py-3 border-b border-[#F6F7F9] text-sm font-body font-medium text-[#0A0A0A]/70 hover:text-[#0B1F3A] transition-colors"
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 mt-6 text-xs tracking-[0.15em] uppercase bg-[#0A0A0A] text-white px-6 py-3 font-body font-semibold"
              >
                Start Your Search <ArrowRight size={11} />
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
